import { useState } from 'react';
import { toast } from 'sonner';
import { admin, apiErrorMessage, type PricingPolicy } from '../../lib/api';
import { useApi } from '../../hooks/useApi';
import { useTranslation } from '../../i18n';
import { DataTable, Button, Modal, StatusBadge, type Column } from '../../components/ui';
import { useAdminT } from './adminI18n';

export default function AdminPricingPage() {
  const t = useAdminT();
  const { t: gt } = useTranslation();
  const [refreshKey, setRefreshKey] = useState(0);
  const [editing, setEditing] = useState<PricingPolicy | null>(null);
  const [draft, setDraft] = useState<Partial<PricingPolicy>>({});
  const [busy, setBusy] = useState(false);

  const list = useApi(() => admin.pricing(false), [refreshKey]);
  const rows = list.status === 'success' ? list.data : [];

  function openEdit(p: PricingPolicy) {
    setEditing(p);
    setDraft({
      name: p.name,
      description: p.description ?? '',
      user_upload_price_usd: p.user_upload_price_usd ?? 0,
      dedupe_share_price_usd: p.dedupe_share_price_usd ?? 0,
      library_price_usd: p.library_price_usd ?? 0,
      is_active: p.is_active ?? true,
    });
  }

  async function save() {
    if (!editing) return;
    setBusy(true);
    try {
      await admin.updatePricing(editing.id, draft);
      toast.success(t('pricing.saved'));
      setEditing(null);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(apiErrorMessage(err, gt));
    } finally {
      setBusy(false);
    }
  }

  const num = (v: unknown) => (v == null ? '—' : `$${Number(v).toFixed(2)}`);
  const columns: Column<PricingPolicy>[] = [
    { key: 'name', header: t('col.name'), render: (r) => r.name },
    { key: 'user_upload_price_usd', header: t('pricing.uploadPrice'), align: 'right', mono: true,
      render: (r) => num(r.user_upload_price_usd) },
    { key: 'is_active', header: t('pricing.active'), width: '100px',
      render: (r) => <StatusBadge status={r.is_active ? 'approved' : 'cancelled'}
        label={r.is_active ? t('pricing.active') : '—'} /> },
    { key: 'is_default', header: t('pricing.default'), width: '80px', render: (r) => (r.is_default ? '★' : '—') },
    { key: 'actions', header: t('col.actions'), width: '90px', align: 'right',
      render: (r) => <Button size="sm" variant="link" onClick={() => openEdit(r)}>{t('action.edit')}</Button> },
  ];

  const field = (key: keyof PricingPolicy, label: string, type = 'text') => (
    <label className="admin-field">
      <span className="admin-field__label">{label}</span>
      <input
        className="prop-choice__text"
        type={type}
        value={String(draft[key] ?? '')}
        onChange={(e) =>
          setDraft((d) => ({ ...d, [key]: type === 'number' ? Number(e.target.value) : e.target.value }))}
      />
    </label>
  );

  return (
    <div className="admin-page">
      <header className="admin-page__head">
        <h1>{t('pricing.title')}</h1>
      </header>

      <DataTable columns={columns} rows={rows} rowKey={(r) => r.id}
        loading={list.status === 'loading'} empty={t('emptyGeneric')} />

      <Modal
        open={editing != null}
        onOpenChange={(o) => { if (!o) setEditing(null); }}
        title={t('pricing.editTitle')}
        size="md"
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setEditing(null)}>{t('action.cancel')}</Button>
            <Button variant="accent" size="sm" disabled={busy} onClick={save}>{t('action.save')}</Button>
          </>
        }
      >
        {field('name', t('col.name'))}
        {field('user_upload_price_usd', t('pricing.uploadPrice'), 'number')}
        {field('dedupe_share_price_usd', t('pricing.sharePrice'), 'number')}
        {field('library_price_usd', t('pricing.libraryPrice'), 'number')}
        <label className="admin-field admin-field--checkbox">
          <input type="checkbox" checked={!!draft.is_active}
            onChange={(e) => setDraft((d) => ({ ...d, is_active: e.target.checked }))} />
          <span>{t('pricing.active')}</span>
        </label>
      </Modal>
    </div>
  );
}
